import { defaultTo } from 'lodash';
import {
  hasInteriorChanged,
  hasPriceOptionsChanged,
  hasVarnishChanged,
  IInterior,
  IOrder,
  IOrderFromFactoryDetails,
  IPimVehicleModel,
  IReference,
  IVarnish,
  IVehicleConfiguration,
  IVehicleInput,
  IVehicleOptionInput,
} from 'next-common';
import { FilterCount, getTTCodeStatusName, IOptionMetaData, IVehicleCountBySeller, IVehicleStock } from '../../common';
import { IVehicleFactoryItem } from '../../components/sales/sales-vehicle-picker-page';
import { mapFactoryItemToVehicleOptionInput, mapVehicleInventoryItemToVehicleOptionInputForPickerPage } from './option';

export const mapFactoryVehicleToVehicleInput = (selectedVehicles, item: IVehicleConfiguration): IVehicleInput => {
  const model = {
    code: null,
    description: null,
    name: item?.model?.name,
    type: item?.salesPackages?.[0]?.name ?? null,
    version: null,
  };

  const orderFromFactoryDetails = {
    colorId: selectedVehicles?.colorId,
    color: selectedVehicles?.color,
    colorPrice: selectedVehicles?.colorPrice,
    interiorId: selectedVehicles?.interiorId,
    interiorCode: selectedVehicles?.interiorCode,
    interiorPrice: selectedVehicles?.interiorPrice,
    expectedProductionDate: selectedVehicles?.expectedProductionDate,
    totalPrice: selectedVehicles?.totalPrice,
  };
  return {
    make: item?.model?.make,
    model,
    serialNo: '',
    series: item?.model?.series,
    vin: '',
    orderFromFactoryDetails,
  };
};

export const getVarnishPrice = (varnish: IVarnish): number => {
  // If the varnish cannot be found in PIM, we don't know the correct price. Set the price to -1 to signal this.
  if (!varnish) {
    return -1;
  }

  // If the varnish is recommended, the price is already included in the vehicle base price.
  if (varnish?.isRecommended) {
    return 0;
  }

  return varnish?.additionalPriceExcludingVat;
};

export const getInteriorPrice = (interior: IInterior): number => {
  if (!interior) {
    return -1;
  }

  if (interior?.isRecommended) {
    return 0;
  }

  return interior?.additionalPriceExcludingVat;
};

export const getFactoryVehicleInput = (
  selectedVehicles: IVehicleFactoryItem,
  vehicle: IVehicleConfiguration
): IVehicleInput => mapFactoryVehicleToVehicleInput(selectedVehicles, vehicle);

export const getOptionInputsForPickerPage = (
  order: IOrder,
  selectedAvlableVehicle: IVehicleStock,
  vehicleConfig: IVehicleConfiguration,
  optionMetaData?: IOptionMetaData
): IVehicleOptionInput[] =>
  selectedAvlableVehicle
    ? [
        mapVehicleInventoryItemToVehicleOptionInputForPickerPage(
          selectedAvlableVehicle,
          order,
          vehicleConfig,
          optionMetaData
        ),
      ]
    : [];

export const getFactoryOptionInputs = (
  order: IOrder,
  selectedVehicles: IVehicleFactoryItem,
  vehicleConfig: IVehicleConfiguration,
  usageCode = null
): IVehicleOptionInput => mapFactoryItemToVehicleOptionInput(selectedVehicles, order, vehicleConfig, usageCode);

export const mapVehicleToFactoryItem = (vehicleItem: IOrderFromFactoryDetails): any[] => {
  const color = vehicleItem?.color ? vehicleItem?.color : null;
  const colorPrice = vehicleItem?.colorPrice ? vehicleItem?.colorPrice : 0;
  const interiorCode = vehicleItem?.interiorCode ? vehicleItem?.interiorCode : null;
  const interiorPrice = vehicleItem?.interiorPrice ? vehicleItem?.interiorPrice : 0;
  const expectedProductionDate = vehicleItem?.expectedProductionDate ? vehicleItem?.expectedProductionDate : null;
  const totalPrice = vehicleItem?.totalPrice ? vehicleItem?.totalPrice : 0;

  return [
    {
      color,
      colorPrice,
      interiorCode,
      interiorPrice,
      expectedProductionDate,
      totalPrice,
    },
  ];
};

/**
 * Aggregates vehicle counts by seller type (own, port/importer, other) based on location IDs and their counts.
 * Returns an object with counts and locationIds for each seller type.
 *
 * @param locationIds - Array of FilterCount objects (code and count)
 * @param handlers - Object with isOwnLocation and isImporterLocation functions
 * @returns Object with own, port, and other seller counts and their locationIds
 */
export const getVehicleCountBySeller = (
  locationIds: FilterCount[],
  { isImporterLocation, isOwnLocation }
): IVehicleCountBySeller => {
  // Note: Label is used as a value here as required
  const result = locationIds?.reduce(
    (acc: IVehicleCountBySeller, { code, count, value }: FilterCount) => {
      if (isOwnLocation(code)) {
        acc.own.own += count;
        acc.own.locationIds.push({ value, label: code });
      } else if (isImporterLocation(code)) {
        acc.port.importer += count;
        acc.port.locationIds.push({ value, label: code });
      } else {
        acc.other.dealer += count;
        acc.other.locationIds.push({ value, label: code });
      }
      return acc;
    },
    {
      own: { own: 0, locationIds: [] },
      port: { importer: 0, locationIds: [] },
      other: { dealer: 0, locationIds: [] },
    }
  );
  return result;
};

export const checkIfVehicleConfigurationHasChanged = (
  updatedVehicleStock: IVehicleStock,
  order: IOrder,
  skuDifference: string[],
  setSku,
  isUserCar: boolean
): boolean => {
  if (isUserCar) return false;
  if (order?.isEditOrder) return false;
  return (
    hasVarnishChanged(updatedVehicleStock, order, skuDifference, setSku) ||
    hasInteriorChanged(updatedVehicleStock, order, skuDifference, setSku) ||
    hasPriceOptionsChanged(updatedVehicleStock, order, setSku)
  );
};
export const validateCarDataFromPIM = (orderReference: IReference, pimData: IPimVehicleModel[]): boolean => {
  let result: boolean[] = [];
  if (pimData?.length) {
    result = pimData.map((config) => config?.sku === orderReference?.pimCode);
  }
  return result?.includes(true);
};

export const getStatusByTTCode = (trackTraceCode: number, vehicleBrand: string) => {
  return getTTCodeStatusName(vehicleBrand, trackTraceCode);
};
//This function only for mocks after change delimeters
export const changeDelimeter = (value = '', from = '-', to = '^') => {
  const regex = new RegExp(from, 'g');
  const newValue = value?.replace(regex, to);
  return newValue;
};

export const normalizeAndLowercase = (str: string): string => {
  const newString = str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '-')
    .toLowerCase();

  return newString;
};

export const mapBrandStringToReadableString = (brand: string) => {
  if (!brand) return '';
  const brandLower = normalizeAndLowercase(brand);

  switch (brandLower) {
    case 'mer':
    case 'mer_p':
    case 'mer_v':
    case 'mercedes':
    case 'mercedes-benz':
    case 'mercedes benz':
      return 'Mercedes-Benz';
    case 'opel':
    case 'ope':
    case 'ope_p':
    case 'ope_v':
      return 'Opel';
    case 'peugeot':
    case 'peu':
    case 'peu_p':
    case 'peu_v':
      return 'Peugeot';
    case 'citroen':
    case 'cit':
    case 'cit_p':
    case 'cit_v':
      return 'Citroën';
    case 'ds':
    case 'ds_p':
    case 'ds_v':
    case 'ds-automobiles':
    case 'ds automobiles':
      return 'DS';
    case 'kia':
    case 'kia_p':
    case 'kia_v':
      return 'Kia';
    case 'smart':
    case 'srt':
    case 'srt_p':
    case 'srt_v':
      return 'Smart';
    case 'fiat':
    case 'fia_p':
    case 'fia_v':
      return 'Fiat';
    case 'fip_p':
    case 'fip_v':
      return 'Fiat professional';
    case 'jeep':
    case 'jee_p':
    case 'jee_v':
      return 'Jeep';
    case 'alf':
    case 'alfa romeo':
      return 'Alfa Romeo';
    case 'fus':
    case 'fuso':
      return 'Fuso';
    default:
      return brand?.charAt(0)?.toUpperCase() + brand?.toLowerCase()?.slice(1);
  }
};

export const getFactoryVehicleInputForMER = (order: IOrder, prodDate: string): IVehicleInput => {
  const model = {
    code: null,
    description: null,
    name: order?.externalPricing?.modelSeries,
    type: null,
    version: null,
  };
  const colorPrice = order?.options?.[0]?.colorPrice;
  const interiorPrice = order?.options?.[0]?.interiorPrice;
  const orderFromFactoryDetails = {
    colorId: order?.options?.[0]?.colorId,
    color: order?.options?.[0]?.colorName,
    colorPrice: order?.options?.[0]?.colorPrice,
    interiorId: order?.options?.[0]?.interiorId,
    interiorCode: `${order?.options?.[0]?.interiorName} / ${order?.options?.[0]?.interiorId}`,
    interiorPrice: order?.options?.[0]?.interiorPrice,
    expectedProductionDate: prodDate,
    totalPrice: Number(defaultTo(order?.pimSnapshot?.price, 0)) + Number(colorPrice) + Number(interiorPrice),
  };
  return {
    make: order?.pimSnapshot?.make,
    model,
    serialNo: '',
    series: order?.pimSnapshot?.series,
    vin: '',
    orderFromFactoryDetails,
  };
};
